// Git Diff Tool - Computes line-level changes for a file
// Extends WorkspaceTool - has access to appType, fs, and git

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

export class GitDiffTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = "gitDiffTool";
  }

  /**
   * Get line-level diff for a file (HEAD vs working tree)
   * @param {string} path - File path relative to workspace root
   * @returns {Promise<Object>} Result with line change information
   */
  async gitDiff(path) {
    try {
      if (!path || typeof path !== "string") {
        return this.returnError({}, "Valid file path required");
      }

      logger.log(
        { tags: "tools|gitdiff|debug", color1: "magenta" },
        "[LINE DIFF DEBUG] gitDiff called",
        { path, appType: this.appType }
      );

      if (this.appType === "browser") {
        return await this.gitDiffForBrowser(path);
      }
      if (this.appType === "local") {
        return await this.gitDiffForLocal(path);
      }

      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for gitDiff`
      );
    } catch (error) {
      logger.error(
        { tags: "tools|gitdiff|error", color1: "red" },
        "Failed to compute git diff",
        error
      );
      return this.returnError({}, error.message);
    }
  }

  async gitDiffForBrowser(path) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    if (!this.git) {
      return this.returnError({}, "Isomorphic-git not available");
    }

    const gitExists = await this.checkGitExists(fs);
    if (!gitExists) {
      logger.log(
        { tags: "tools|gitdiff|debug", color1: "magenta" },
        "[LINE DIFF DEBUG] gitDiff aborted - no git repo"
      );
      return this.returnSuccess(
        { hasGit: false, changes: null },
        "No git repository found"
      );
    }

    const normalizedPath = this.normalizeAndValidatePath(path);
    if (!normalizedPath) {
      return this.returnError({}, "Invalid file path");
    }

    const filepath = normalizedPath.replace(/^\/+/, "");
    const workdirPath = `/files/${filepath}`;

    const [fileStatus] = await this.git.statusMatrix({
      fs: this.fs,
      dir: "/files",
      filepaths: [filepath],
    });

    if (!fileStatus) {
      logger.log(
        { tags: "tools|gitdiff|debug", color1: "magenta" },
        "[LINE DIFF DEBUG] No status matrix entry for file",
        { filepath }
      );
      return this.returnSuccess(
        { isBinary: false, changes: null },
        "File not tracked by git status"
      );
    }

    const [, headStatus, workdirStatus] = fileStatus;

    // Read HEAD content
    const headContent = await this.readHeadContent(filepath);
    // Read working tree content
    const workdirContent = await this.readWorkdirContent(workdirPath);

    let beforeText = this.decodeTextContent(headContent);
    let afterText = this.decodeTextContent(workdirContent);

    logger.log(
      { tags: "tools|gitdiff|debug", color1: "magenta" },
      "[LINE DIFF DEBUG] Status + content summary",
      {
        filepath,
        headStatus,
        workdirStatus,
        hasHeadText: beforeText !== null,
        hasWorkdirText: afterText !== null,
      }
    );

    // Handle binary files
    if (beforeText === null && afterText === null) {
      return this.returnSuccess(
        { isBinary: true, changes: null },
        "Binary or unreadable file"
      );
    }

    // Handle added/deleted files based on git status
    if (headStatus === 0 && workdirStatus !== 0) {
      beforeText = "";
    } else if (workdirStatus === 0 && headStatus !== 0) {
      afterText = "";
    }

    // If file is tracked but HEAD content can't be read, skip diff
    if (headStatus !== 0 && beforeText === null) {
      logger.log(
        { tags: "tools|gitdiff|debug", color1: "magenta" },
        "[LINE DIFF DEBUG] Tracked file but HEAD content unavailable",
        { filepath }
      );
      return this.returnSuccess(
        { isBinary: false, changes: null },
        "Unable to read HEAD content for tracked file"
      );
    }

    const changes = this.computeLineChanges(beforeText || "", afterText || "");

    logger.log(
      { tags: "tools|gitdiff|debug", color1: "magenta" },
      "[LINE DIFF DEBUG] Line changes computed",
      {
        filepath,
        added: changes.addedLines.length,
        modified: changes.modifiedLines.length,
        deleted: changes.deletedLines.length,
      }
    );

    return this.returnSuccess(
      {
        isBinary: false,
        changes,
        path: filepath,
      },
      "Diff computed successfully"
    );
  }

  /**
   * HEAD vs working tree diff for `local` apps (desktop runs real `git`).
   */
  async gitDiffForLocal(path) {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.gitDiffWorkspace) {
      return this.returnError(
        {},
        "Local git diff requires Revolt Desktop (workspaceLocalAppAPI.gitDiffWorkspace)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const normalizedPath = this.normalizeAndValidatePath(path);
    if (!normalizedPath) {
      return this.returnError({}, "Invalid file path");
    }

    const filepath = normalizedPath.replace(/^\/+/, "");

    const res = await api.gitDiffWorkspace({
      rootPath: root,
      relativePath: filepath,
    });

    if (!res?.success) {
      if (res?.gitNotInstalled) {
        return this.returnError(
          { gitNotInstalled: true },
          res.error ||
            "Git is not installed. Install Git and make sure it's on your PATH to use source control in local workspaces."
        );
      }
      return this.returnError({}, res?.error || "Failed to compute git diff");
    }

    if (!res.hasGit) {
      return this.returnSuccess(
        { hasGit: false, changes: null },
        "No git repository found"
      );
    }

    if (res.isBinary) {
      return this.returnSuccess(
        { isBinary: true, changes: null, path: filepath },
        "Binary or unreadable file"
      );
    }

    let beforeText = res.headText ?? "";
    let afterText = res.workdirText ?? "";

    const inHead = !!res.inHead;
    const workdirExists = !!res.workdirExists;

    if (!inHead && workdirExists) {
      beforeText = "";
    } else if (inHead && !workdirExists) {
      afterText = "";
    }

    const changes = this.computeLineChanges(beforeText || "", afterText || "");

    return this.returnSuccess(
      {
        isBinary: false,
        changes,
        path: filepath,
      },
      "Diff computed successfully"
    );
  }

  async readHeadContent(filepath) {
    if (!this.git?.readBlob) {
      return null;
    }

    try {
      const result = await this.git.readBlob({
        fs: this.fs,
        dir: "/files",
        filepath,
        ref: "HEAD",
      });
      return result?.blob || result?.object || result || null;
    } catch (error) {
      logger.log(
        { tags: "tools|gitdiff|debug", color1: "magenta" },
        "[LINE DIFF DEBUG] readBlob(ref=HEAD) failed, attempting tree lookup",
        { filepath, error: error?.message }
      );
    }

    return await this.readHeadContentFromTree(filepath);
  }

  async readHeadContentFromTree(filepath) {
    if (!this.git?.resolveRef || !this.git?.readCommit || !this.git?.readTree) {
      return null;
    }

    try {
      const headOid = await this.git.resolveRef({
        fs: this.fs,
        dir: "/files",
        ref: "HEAD",
      });
      const commit = await this.git.readCommit({
        fs: this.fs,
        dir: "/files",
        oid: headOid,
      });
      const treeOid = commit?.commit?.tree;
      if (!treeOid) {
        return null;
      }

      const entry = await this.findTreeEntry(treeOid, filepath);
      if (!entry?.oid || entry.type !== "blob") {
        return null;
      }

      const result = await this.git.readBlob({
        fs: this.fs,
        dir: "/files",
        oid: entry.oid,
      });
      return result?.blob || result?.object || result || null;
    } catch (error) {
      logger.log(
        { tags: "tools|gitdiff|debug", color1: "magenta" },
        "[LINE DIFF DEBUG] readHeadContentFromTree failed",
        { filepath, error: error?.message }
      );
      return null;
    }
  }

  async findTreeEntry(treeOid, filepath) {
    const parts = filepath.split("/").filter(Boolean);
    let currentTreeOid = treeOid;

    for (let index = 0; index < parts.length; index += 1) {
      const part = parts[index];
      const treeResult = await this.git.readTree({
        fs: this.fs,
        dir: "/files",
        oid: currentTreeOid,
      });
      const entries = treeResult?.tree || treeResult || [];
      const entry = entries.find((item) => item?.path === part);

      if (!entry) {
        return null;
      }

      const isLast = index === parts.length - 1;
      if (isLast) {
        return entry;
      }

      if (entry.type !== "tree") {
        return null;
      }

      currentTreeOid = entry.oid;
    }

    return null;
  }

  async readWorkdirContent(workdirPath) {
    try {
      return await this.fs.promises.readFile(workdirPath);
    } catch (error) {
      if (error.code === "ENOENT") {
        return null;
      }
      throw error;
    }
  }

  decodeTextContent(content) {
    if (!content) {
      return null;
    }

    let buffer = content;
    if (content?.buffer) {
      buffer = content;
    } else if (Array.isArray(content)) {
      buffer = new Uint8Array(content);
    }

    try {
      const text = new TextDecoder("utf-8").decode(buffer);
      if (text.includes("\u0000")) {
        return null;
      }
      return text;
    } catch (error) {
      return null;
    }
  }

  computeLineChanges(beforeText, afterText) {
    const beforeLines = beforeText.split("\n");
    const afterLines = afterText.split("\n");
    const diffLines = this.buildLineDiff(beforeLines, afterLines, 20000);

    const addedLines = [];
    const modifiedLines = [];
    const deletedLines = [];

    let afterIndex = 0;
    let pendingRemovals = 0;

    const flushRemovals = () => {
      if (pendingRemovals > 0) {
        const lineNumber = Math.max(1, afterIndex + 1);
        deletedLines.push(lineNumber);
        pendingRemovals = 0;
      }
    };

    for (const line of diffLines) {
      const marker = line[0];
      if (marker === " ") {
        flushRemovals();
        afterIndex += 1;
        continue;
      }
      if (marker === "-") {
        pendingRemovals += 1;
        continue;
      }
      if (marker === "+") {
        if (pendingRemovals > 0) {
          modifiedLines.push(afterIndex + 1);
          pendingRemovals -= 1;
        } else {
          addedLines.push(afterIndex + 1);
        }
        afterIndex += 1;
        continue;
      }
    }

    flushRemovals();

    return { addedLines, modifiedLines, deletedLines };
  }

  buildLineDiff(beforeLines, afterLines, maxLines) {
    const m = beforeLines.length;
    const n = afterLines.length;

    // For very large files, use a simpler approach to find meaningful changes
    if (m + n > 1000) {
      return this.buildSimpleLineDiff(beforeLines, afterLines, maxLines);
    }

    // LCS-based diff
    const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
    for (let i = m - 1; i >= 0; i -= 1) {
      for (let j = n - 1; j >= 0; j -= 1) {
        if (beforeLines[i] === afterLines[j]) {
          dp[i][j] = dp[i + 1][j + 1] + 1;
        } else {
          dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
        }
      }
    }

    const diff = [];
    let i = 0;
    let j = 0;
    while (i < m && j < n) {
      if (beforeLines[i] === afterLines[j]) {
        diff.push(` ${beforeLines[i]}`);
        i += 1;
        j += 1;
      } else if (dp[i + 1][j] >= dp[i][j + 1]) {
        diff.push(`-${beforeLines[i]}`);
        i += 1;
      } else {
        diff.push(`+${afterLines[j]}`);
        j += 1;
      }
      if (diff.length > maxLines) {
        return diff.slice(0, maxLines);
      }
    }

    while (i < m) {
      diff.push(`-${beforeLines[i]}`);
      i += 1;
      if (diff.length > maxLines) {
        return diff.slice(0, maxLines);
      }
    }
    while (j < n) {
      diff.push(`+${afterLines[j]}`);
      j += 1;
      if (diff.length > maxLines) {
        return diff.slice(0, maxLines);
      }
    }

    return diff;
  }

  buildSimpleLineDiff(beforeLines, afterLines, maxLines) {
    const diff = [];
    const maxLinesToCompare = Math.min(
      beforeLines.length,
      afterLines.length,
      200
    );
    let changesFound = 0;

    for (let i = 0; i < maxLinesToCompare; i += 1) {
      if (beforeLines[i] !== afterLines[i]) {
        if (i < beforeLines.length) {
          diff.push(`-${beforeLines[i]}`);
        }
        if (i < afterLines.length) {
          diff.push(`+${afterLines[i]}`);
        }
        changesFound += 1;
      } else {
        diff.push(` ${beforeLines[i]}`);
      }
      if (diff.length > maxLines) {
        return diff.slice(0, maxLines);
      }
    }

    if (beforeLines.length > maxLinesToCompare) {
      diff.push(
        `\n[Additional ${beforeLines.length - maxLinesToCompare} lines removed]`
      );
    }
    if (afterLines.length > maxLinesToCompare) {
      diff.push(
        `\n[Additional ${afterLines.length - maxLinesToCompare} lines added]`
      );
    }

    return changesFound > 0 ? diff : [];
  }

  normalizeAndValidatePath(path) {
    if (!path || typeof path !== "string") {
      return null;
    }

    let normalized = path.replace(/^\/+/, "");
    if (normalized.includes("..")) {
      return null;
    }
    normalized = normalized.replace(/\\/g, "/");
    return `/${normalized}`;
  }

  async checkGitExists(fs) {
    try {
      const stats = await fs.stat("/files/.git");
      const isDir =
        typeof stats.isDirectory === "function"
          ? stats.isDirectory()
          : stats.type === "dir";
      return isDir;
    } catch (error) {
      return false;
    }
  }
}
